import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAngleLeft,
  faAngleRight,
  faDownload,
  faSearch,
  faSort,
  faSortDown,
  faSortUp,
  faSyncAlt,
} from "@fortawesome/free-solid-svg-icons";
import {
  exportSettlementHistoryCsv,
  getSettlementHistory,
} from "../../services/settlementApi";

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatCurrency = (value) => {
  const n = Number(value ?? 0);
  if (Number.isNaN(n)) return value || "";
  return new Intl.NumberFormat("en-PH", {
    style: "currency",
    currency: "PHP",
    minimumFractionDigits: 2,
  }).format(n);
};

const SortIcon = ({ col, sortBy, sortDir }) => {
  if (sortBy !== col) return <FontAwesomeIcon icon={faSort} className="ml-1 text-gray-300" />;
  return sortDir === "asc"
    ? <FontAwesomeIcon icon={faSortUp} className="ml-1 text-blue-400" />
    : <FontAwesomeIcon icon={faSortDown} className="ml-1 text-blue-400" />;
};

const StatusBadge = ({ value }) => {
  switch (value) {
    case "Paid":       return <span className="px-2 py-0.5 rounded text-xs font-semibold bg-green-100 text-green-700">Paid</span>;
    case "Incomplete": return <span className="px-2 py-0.5 rounded text-xs font-semibold bg-yellow-100 text-yellow-700">Incomplete</span>;
    default:           return <span className="px-2 py-0.5 rounded text-xs font-semibold bg-red-100 text-red-700">Unpaid</span>;
  }
};

// ─── Component ───────────────────────────────────────────────────────────────

const SettlementHistoryPage = () => {
  // ── Table state ──
  const [rows, setRows]             = useState([]);
  const [loading, setLoading]       = useState(false);
  const [search, setSearch]         = useState("");
  const [appliedSearch, setApplied] = useState("");
  const [dateFrom, setDateFrom]     = useState("");
  const [dateTo, setDateTo]         = useState("");
  const [appliedDateFrom, setAppliedDateFrom] = useState("");
  const [appliedDateTo, setAppliedDateTo]     = useState("");
  const [sortBy, setSortBy]         = useState("due_date");
  const [sortDir, setSortDir]       = useState("desc");
  const [page, setPage]             = useState(1);
  const [perPage, setPerPage]       = useState(10);
  const [pagination, setPagination] = useState({});

  // ─── Fetch data ──────────────────────────────────────────────────────────────

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getSettlementHistory({
        search:         appliedSearch,
        date_from:      appliedDateFrom,
        date_to:        appliedDateTo,
        sort_by:        sortBy,
        sort_direction: sortDir,
        page,
        per_page:       perPage,
      });
      setRows(res.data.data ?? []);
      setPagination(res.data.pagination ?? {});
    } catch {
      toast.error("Failed to load settlement history.");
    } finally {
      setLoading(false);
    }
  }, [appliedSearch, appliedDateFrom, appliedDateTo, sortBy, sortDir, page, perPage]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ─── Sort ────────────────────────────────────────────────────────────────────

  const handleSort = (col) => {
    if (sortBy === col) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(col);
      setSortDir("desc");
    }
    setPage(1);
  };

  // ─── Search / Filter ─────────────────────────────────────────────────────────

  const handleApply = () => {
    setApplied(search);
    setAppliedDateFrom(dateFrom);
    setAppliedDateTo(dateTo);
    setPage(1);
  };

  const handleSearchKeyDown = (e) => { if (e.key === "Enter") handleApply(); };

  // ─── CSV Export ──────────────────────────────────────────────────────────────

  const handleExportCsv = async () => {
    try {
      const res = await exportSettlementHistoryCsv({
        search:         appliedSearch,
        date_from:      appliedDateFrom,
        date_to:        appliedDateTo,
        sort_by:        sortBy,
        sort_direction: sortDir,
      });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a   = document.createElement("a");
      a.href = url;
      a.download = `settlement_history_${Date.now()}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("CSV export failed.");
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────────

  const totalPages = pagination.last_page ?? 1;

  const columns = [
    ["payment_ref_no", "Payment Ref #"],
    ["due_date",       "Due Date"],
    ["provider_or_no", "Provider OR No."],
    ["provider_name",  "Provider"],
    ["net_payable",    "Net Payable"],
    ["check_no",       "Check Number"],
    ["credited_date",  "Date Credited"],
    ["tat",            "TAT"],
    ["status",         "Status"],
  ];

  return (
    <div className="p-4 min-h-screen bg-gray-50">

      {/* ── Page Header ── */}
      <div className="mb-4 flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-bold text-gray-800">Settlement History</h1>
          <p className="text-sm text-gray-500">Provider Payment &rsaquo; Settlement History (Paid records)</p>
        </div>
        <button onClick={handleExportCsv}
          className="flex items-center gap-1 px-3 py-1.5 text-sm bg-gray-600 text-white rounded hover:bg-gray-700">
          <FontAwesomeIcon icon={faDownload} /> Export CSV
        </button>
      </div>

      {/* ── Filters ── */}
      <div className="mb-4 flex flex-wrap gap-2 items-center">
        {/* Date range */}
        <div className="flex items-center gap-1 text-sm text-gray-600">
          <span>Due Date From:</span>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
            className="border rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white" />
        </div>
        <div className="flex items-center gap-1 text-sm text-gray-600">
          <span>To:</span>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
            className="border rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white" />
        </div>
        {/* Search */}
        <div className="flex">
          <input value={search} onChange={(e) => setSearch(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            placeholder="Search..."
            className="border rounded-l px-3 py-1.5 text-sm w-56 focus:outline-none focus:ring-1 focus:ring-blue-400" />
          <button onClick={handleApply}
            className="px-3 py-1.5 bg-blue-600 text-white rounded-r hover:bg-blue-700">
            <FontAwesomeIcon icon={faSearch} />
          </button>
        </div>
        <select value={perPage} onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
          className="border rounded px-2 py-1.5 text-sm bg-white">
          {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n} / page</option>)}
        </select>
        <button onClick={fetchData}
          className="flex items-center gap-1 px-3 py-1.5 text-sm bg-white border rounded hover:bg-gray-100">
          <FontAwesomeIcon icon={faSyncAlt} /> Refresh
        </button>
      </div>

      {/* ── Table ── */}
      <div className="overflow-x-auto rounded-xl shadow bg-white">
        <table className="min-w-full text-xs divide-y divide-gray-200">
          <thead className="bg-gray-100 sticky top-0 z-10">
            <tr>
              {columns.map(([col, label]) => (
                <th key={col}
                  className="px-3 py-2 text-left font-semibold text-gray-600 whitespace-nowrap cursor-pointer hover:bg-gray-200 select-none"
                  onClick={() => handleSort(col)}>
                  {label}<SortIcon col={col} sortBy={sortBy} sortDir={sortDir} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading && (
              <tr><td colSpan={columns.length} className="text-center py-8 text-gray-400">Loading...</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={columns.length} className="text-center py-8 text-gray-400">No paid records found.</td></tr>
            )}
            {!loading && rows.map((row, idx) => (
              <tr key={row.tbl_id ?? idx} className="hover:bg-sky-50 transition-colors">
                <td className="px-3 py-1.5 whitespace-nowrap">{row.payment_ref_no || ""}</td>
                <td className="px-3 py-1.5 whitespace-nowrap">{row.due_date || ""}</td>
                <td className="px-3 py-1.5 whitespace-nowrap">{row.provider_or_no || ""}</td>
                <td className="px-3 py-1.5 whitespace-nowrap max-w-[220px] truncate" title={row.provider_name}>{row.provider_name || ""}</td>
                <td className="px-3 py-1.5 whitespace-nowrap text-right font-medium">{row.net_payable != null ? formatCurrency(row.net_payable) : ""}</td>
                <td className="px-3 py-1.5 whitespace-nowrap">{row.check_no || ""}</td>
                <td className="px-3 py-1.5 whitespace-nowrap">{row.credited_date || ""}</td>
                <td className="px-3 py-1.5 whitespace-nowrap">{row.tat ?? "0"}</td>
                <td className="px-3 py-1.5 whitespace-nowrap"><StatusBadge value={row.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Pagination ── */}
      <div className="mt-4 flex items-center justify-between text-sm text-gray-600 flex-wrap gap-2">
        <span>
          {pagination.from ?? 0}–{pagination.to ?? 0} of {pagination.total ?? 0} records
        </span>
        <div className="flex items-center gap-1">
          <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}
            className="px-2 py-1 border rounded disabled:opacity-50 hover:bg-gray-100">
            <FontAwesomeIcon icon={faAngleLeft} />
          </button>
          <span className="px-3">Page {page} / {totalPages}</span>
          <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
            className="px-2 py-1 border rounded disabled:opacity-50 hover:bg-gray-100">
            <FontAwesomeIcon icon={faAngleRight} />
          </button>
        </div>
      </div>

    </div>
  );
};

export default SettlementHistoryPage;
